import { useEffect } from 'react';
import { Container } from 'theme-ui';
import { useSettings } from '@backpackjs/storefront';

import {
  useDataLayerActions,
  usePickedSections,
  useGatedProduct,
} from '@hooks';
import store, { useRecoilValue, useSetRecoilState } from '@store';

import { Breadcrumbs } from '@snippets';
import { CollectionGrid } from './CollectionGrid';
import { SeoFooter } from './SeoFooter';
import { DesktopFilters, MobileFilters } from './Filters';
import { useCollection, useCollectionFilters } from './hooks';

function CollectionComponent({ collection, sections }) {
  const settings = useSettings();
  const gatedPage = useRecoilValue(store.gatedPage);
  const { sendViewCollectionEvent } = useDataLayerActions();

  const { gatedProducts } = useGatedProduct({
    gatedPage,
    currentUrl: collection.handle,
  });

  const { firstOf, otherSections } = usePickedSections({
    firstOf: ['collection-hero', 'collection-categories'],
    sections,
  });
  const { products, merchandising } = useCollection();
  const [, { clearSelectedFilters }] = useCollectionFilters();

  const setSwatchesMap = useSetRecoilState(store.swatchesMap);

  const swatches = settings?.collection?.colors?.swatches || [];

  useEffect(() => {
    const _swatchesMap = swatches.reduce((map, swatch) => {
      map[swatch.name?.toLowerCase()] = swatch;
      return map;
    }, {});
    setSwatchesMap(_swatchesMap);
  }, [JSON.stringify(swatches)]);

  useEffect(() => {
    return () => {
      clearSelectedFilters();
    };
  }, [collection?.handle]);

  useEffect(() => {
    sendViewCollectionEvent({
      products: collection?.products,
      collectionId: collection?.id,
    });
  }, [
    `${collection?.id}${!!collection?.products?.length}`,
    sendViewCollectionEvent,
  ]);

  return (
    <Container data-comp="CollectionTemplate">
      {firstOf['collection-hero'] || null}

      <Breadcrumbs isCollection collectionTitle={collection?.title} />

      {otherSections}

      {!gatedProducts && (
        <MobileFilters
          collection={collection}
          hasHero={!!firstOf['collection-hero']}
          collectionCategories={firstOf['collection-categories'] || null}
        />
      )}

      {/* Product items grid */}
      <Container
        sx={{
          variant: 'layout.content',
          display: gatedProducts ? 'block' : 'grid',
          gridTemplateColumns: ['1fr', '200px 1fr'],
          gridTemplateAreas: [
            '"collection" "footer"',
            '"title collection" "filters collection" "filters footer"',
          ],
          gridTemplateRows: 'min-content 1fr',
          columnGap: 6,
          rowGap: 0,
          pt: [0, 14, 15],
          pb: [14, 18, '80px'],
          alignItems: 'flex-start',
        }}
      >
        {!gatedProducts && (
          <DesktopFilters
            collection={collection}
            hasHero={!!firstOf['collection-hero']}
            collectionCategories={firstOf['collection-categories'] || null}
            merchandising={merchandising}
          />
        )}

        <CollectionGrid
          products={gatedProducts || products}
          merchandising={merchandising}
          hasInitialProducts={!!collection.products?.length}
          gatedPage={gatedPage}
        />

        <SeoFooter />
      </Container>
    </Container>
  );
}

export function CollectionTemplate({ collection, renderSections }) {
  const sections = renderSections();
  return <CollectionComponent sections={sections} collection={collection} />;
}
